import { useEffect, useRef, useState } from "react";
import { useSiteConfig } from "@/hooks";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useGetGuestBookingDataQuery, useGetGuestQuoteByIdQuery } from "@/redux/apis/guest/guestApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { restoreBookingDraft } from "@/redux/slices/clientBooking/clientBookingSlice";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { hydrateDraftFromGuestQuote } from "./hydrateDraftFromGuestQuote";

export type QuotePrefillStatus = "loading" | "resolved" | "not-found";

interface UseHydrateBookingFromQuoteOptions {
  /** The `quote_uuid` from the widget handoff. Absent → nothing to resolve → "not-found". */
  quoteUuid?: string;
  /**
   * Resolve the operator FROM the quote's `provider_code` (public `verifyProvider` → `setActiveOperator`).
   * The guest flow and the authenticated client self-service flow both need this — the quote carries the
   * operator. The operator OBO flow already has the operator fixed by the `:operatorId` route, so it passes
   * `false` and the hook only hydrates the draft.
   */
  resolveOperator: boolean;
  /**
   * Skip hydrating the draft from the quote — used when the caller restores a stashed draft instead
   * (a guest returning from an ACCOUNT_EXISTS 409 → login). Operator resolution still runs.
   */
  skipHydration?: boolean;
}

/**
 * Rehydrate the shared booking draft from a widget quote handoff (`?quote_uuid=…`).
 *
 * Lifted out of `GuestBooking` so all three entry points can prefill from a quote: the unauthenticated
 * guest flow, the authenticated client self-service flow, and the operator OBO flow. The quote-fetch and
 * the pure `hydrateDraftFromGuestQuote` mapper are operator-agnostic; the only difference between callers
 * is whether the operator is resolved from the quote (guest / client) or already fixed by the route (OBO).
 *
 * The guest quote + job-data endpoints are public, so they work identically whether or not there's a
 * session — no authenticated twin is needed for prefill.
 */
export const useHydrateBookingFromQuote = ({
  quoteUuid,
  resolveOperator,
  skipHydration = false,
}: UseHydrateBookingFromQuoteOptions): { status: QuotePrefillStatus } => {
  const dispatch = useAppDispatch();
  const { apiUrl } = useSiteConfig();
  const [verify] = useLazyVerifyProviderQuery();
  const providerUuid = useAppSelector(operatorIdSelector);

  // When the operator is already fixed by the route (OBO) there's nothing to resolve, so start resolved.
  const [status, setStatus] = useState<QuotePrefillStatus>(resolveOperator ? "loading" : "resolved");

  // Public, operator-independent read: the quote carries the operator (provider_code) and the inputs to
  // rehydrate the draft.
  const { data: quoteData, isError: isQuoteError } = useGetGuestQuoteByIdQuery(quoteUuid ?? "", { skip: !quoteUuid });
  const { data: quoteBookingData, isError: isJobDataError } = useGetGuestBookingDataQuery(providerUuid ?? "", {
    skip: !providerUuid,
  });

  // Resolve the operator FROM the quote's provider_code, then store it active. No quote / bad quote /
  // a quote with no operator → not-available.
  useEffect(() => {
    if (!resolveOperator) return;
    if (!quoteUuid || isQuoteError || (quoteData && !quoteData.provider_code)) {
      setStatus("not-found");
      return;
    }
    const code = quoteData?.provider_code;
    if (!code) return; // still loading the quote
    let cancelled = false;
    verify({ providerCode: code, apiUrl })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        dispatch(setActiveOperator(res));
        setStatus("resolved");
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("(useHydrateBookingFromQuote) Error verifying provider:", { code, error: e });
        setStatus("not-found");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveOperator, quoteUuid, quoteData, isQuoteError, apiUrl]);

  // Hydrate the draft once from the quote. Wait for job-data (it enriches vehicle/transfer/extras) UNLESS
  // it errored — a job-data outage must not drop the whole prefill, so fall through with it undefined;
  // locations + datetime still restore.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (skipHydration || hydratedRef.current || !quoteData) return;
    if (!quoteBookingData && !isJobDataError) return;
    const draft = hydrateDraftFromGuestQuote(quoteData.request, quoteBookingData);
    if (draft) dispatch(restoreBookingDraft(draft));
    hydratedRef.current = true;
  }, [skipHydration, quoteData, quoteBookingData, isJobDataError, dispatch]);

  return { status };
};
